import { LoadingSpinner, Tabs, Typography } from '@components';
import { useCompany } from '@hooks';
import { NetworkAdapterInterface } from '@interfaces';
import { AssetsService } from '@services';
import { Capitalize } from '@utils';
import { Popconfirm } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { addToast } from '../../state/toast/ToastSlice';

import {
  NetworkInformation,
  ProductStorage,
  RmmDeviceInfo,
  WindowsActivities,
  WindowsApps,
  WindowsServices,
} from '@containers';

const ProductInfo: FC = ({ asset, category }) => {
  const { t } = useTranslation();
  const [formLoading, setFormLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [assetData, setAssetData] = useState();
  const [networkInterfaces, setNetworkInterfaces] = useState<
    NetworkAdapterInterface[]
  >([]);
  const { companyId } = useCompany();
  const service = new AssetsService();
  const dispatch = useDispatch();

  useEffect(() => {
    setFormLoading(true);

    const fetchData = async () => {
      const assetData = await service.assetDetail(companyId, asset.ordSer_id);
      setAssetData(assetData);
      setFormLoading(false);
    };

    const fetchInterfaces = async () => {
      const interfaces = await service.fetchNinjaNetworkInterfaces(
        companyId,
        asset.ordSer_id
      );
      setNetworkInterfaces(interfaces);
    };

    if (asset.device_id) {
      fetchData();
      fetchInterfaces();
    } else {
      setFormLoading(false);
    }
  }, [asset]);

  const rebootDevice = async (device_id: number) => {
    setConfirmLoading(true);
    try {
      // await service.rebootNinjaDevice(device_id);
      dispatch(
        addToast({
          description: 'Het apparaat is verzocht te herstarten.',
          position: 'bottomRight',
          style: 'success',
        })
      );
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const tabs = [
    {
      nav: 'Opslag',
      component: [
        <ProductStorage volumes={assetData?.ninjaDeviceInfo?.volumes} />,
      ],
    },
  ];

  if (assetData?.ninjaDeviceInfo?.nodeClass === 'WINDOWS_WORKSTATION') {
    tabs.push(
      {
        nav: 'Windows services',
        component: [
          <WindowsServices
            deviceId={asset.device_id}
            assetId={asset.ordSer_id}
          />,
        ],
      },
      {
        nav: 'Netwerkadapters',
        component: [
          <NetworkInformation
            publicIp={assetData?.ninjaDeviceInfo?.publicIP ?? 'Onbekend'}
            interfaces={networkInterfaces}
          />,
        ],
      },
      {
        nav: 'Windows apps',
        component: [<WindowsApps assetId={asset.ordSer_id} />],
      },
      {
        nav: 'Windows activiteiten',
        component: [<WindowsActivities assetId={asset.ordSer_id} />],
      }
    );
  }

  if (formLoading) return <LoadingSpinner />;

  if (assetData?.ninjaDeviceInfo)
    return (
      <div className="grid grid-cols-2 gap-6">
        <RmmDeviceInfo
          category={category}
          deviceInfo={assetData.ninjaDeviceInfo}
        />

        {assetData?.ninjaDeviceInfo?.offline ? (
          ''
        ) : (
          <div>
            <Popconfirm
              title={t('Herstart apparaat')}
              description={t('Wilt u dit apparaat herstarten?')}
              onConfirm={() => rebootDevice(asset.device_id)}
              okText={t('Herstart')}
              cancelText={t('Annuleer')}
              okButtonProps={{ loading: confirmLoading }}
              okType="danger"
              icon={null}
            >
              <button className="px-3 py-1 rounded-lg border border-red-600 text-red-600 hover:text-red-800">
                {t('Herstarten')}
              </button>
            </Popconfirm>
          </div>
        )}
        <div className="col-span-2">
          <Tabs tabs={tabs} />
        </div>
      </div>
    );

  return (
    <div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Typography tag={'p'} type={'semibold'}>
            Naam
          </Typography>
          <Typography tag={'p'} type={'default'}>
            {asset?.ordPro_naam ?? '/'}
          </Typography>
        </div>
        <div>
          <Typography tag={'p'} type={'semibold'}>
            Serienummer
          </Typography>
          <Typography tag={'p'} type={'default'}>
            {asset?.ordSer_serial ?? '/'}
          </Typography>
        </div>

        <div>
          <Typography tag={'p'} type={'semibold'}>
            Gebruiker
          </Typography>
          <Typography tag={'p'} type={'default'}>
            {Capitalize(asset.ordSer_username ?? '/')}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;
